/*
 * BadgeMovementForm.tsx : Un formulaire pour ajouter ou modifier un mouvement de badge.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faAddressCard,
	faArrowRightArrowLeft,
	faBuilding,
	faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
// Import Communs et Layout
import {
	DatePickerField,
	formatLocalDateTime,
	finishFormSave,
	parseLocalDate,
	FormSaveActions,
	InputField,
	SecondaryButton,
	SelectField,
	TextAreaField,
	useFormDirty,
} from '../../../common';
import CardHeader from 'layout/CardHeader';
import CardSection from 'layout/CardSection';

// Import BadgeMovement
import { BadgeMovementData } from '../types/types';
import { badgeMovementsApi } from '../services/badgeMovementsApi';

// Import Connexions externes
import {
	ExternalConnectionData,
	externalConnectionsApi,
} from '../../externalConnection';

// Import activities
import { ActivityData, activitiesApi } from '../../activities';

// Composant BadgeMovementForm
const BadgeMovementForm: React.FC = () => {
	// Hook de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hook de Formulaire
	const [formData, setFormData] = React.useState<BadgeMovementData>({
		id: 0,
		originalBadgeNumber: '',
		ownerFirstName: '',
		ownerLastName: '',
		company: '',
		status: '',
		externalConnection: { id: 0, name: '' } as ExternalConnectionData,
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hooks pour le select des connexions externes
	const [externalConnectionOptions, setExternalConnectionOptions] =
		React.useState<{ key: string; label: string }[]>([]);
	const [loadingExternalConnections, setLoadingExternalConnections] =
		React.useState(true);

	// Hooks pour le select des activités
	const [activityOptions, setActivityOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingActivities, setLoadingActivities] = React.useState(true);

	// Hook pour le select de direction (simplement IN ou OUT)
	const [directionOptions] = React.useState([
		{ key: 'IN', label: 'IN' },
		{ key: 'OUT', label: 'OUT' },
	]);

	// Hook pour l'état (simplement IMPORTED ou MANUAL)
	const [statusOptions] = React.useState([
		{ key: 'IMPORTED', label: 'IMPORTED' },
		{ key: 'MANUAL', label: 'MANUAL' },
	]);

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si un id est présent, on fetch les données
		if (id) {
			badgeMovementsApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// Préremplir les selects
					if (data.externalConnection) {
						setExternalConnectionOptions([
							{
								key: data.externalConnection.id.toString(),
								label: data.externalConnection.name,
							},
						]);
					}
					if (data.activity) {
						setActivityOptions([
							{
								key: data.activity.id.toString(),
								label: 'ACT-' + data.activity.id.toString(),
							},
						]);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Sinon, pas d'id, on arrête le loading
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Lazy load des options de connexions externes
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingExternalConnections) {
			externalConnectionsApi
				.listSelect(token)
				.then((items) => {
					setExternalConnectionOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.name),
						}))
					);
				})
				.catch((error) => console.error(error))
				.finally(() => setLoadingExternalConnections(false));
		}
	}, [loading, loadingExternalConnections]);

	// Lazy load des options d'activités
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingActivities) {
			activitiesApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					setActivityOptions(
						res.data.map((activity: ActivityData) => ({
							key: activity.id.toString(),
							label: 'ACT-' + activity.id.toString(),
						}))
					);
				})
				.catch((error) => console.error(error))
				.finally(() => setLoadingActivities(false));
		}
	}, [loading, loadingActivities]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean, forceCreate = false) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		// Déterminer si c'est une mise à jour ou une création
		const isUpdate = !forceCreate && id;
		// on supprime importedAt pour éviter les conflits lors de la mise à jour
		const keysToRemove = ['importedAt'] as const;

		const dataToUpdate = Object.fromEntries(
			Object.entries(formData).filter(
				([key]) => !keysToRemove.includes(key as (typeof keysToRemove)[number])
			)
		);
		const onLeave = () => navigate('/app/badgeMovements');
		// Si un id est présent, mise à jour de l'élément existant
		if (isUpdate) {
			badgeMovementsApi
				.update(Number(id), dataToUpdate, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			badgeMovementsApi
				.create(dataToUpdate, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(`/app/badgeMovements/${created.id}/edit`, {
											replace: true,
										})
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e?: React.FormEvent, forceCreate = false) => {
		// Empêcher le comportement par défaut du formulaire
		e?.preventDefault();
		handleSave(true, forceCreate);
	};

	// Handle Formulaire : Changements des inputs et textareas
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		// Si name est externalConnection, on doit mettre à jour l'objet complet
		if (name === 'externalConnection') {
			const selectedOption = externalConnectionOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					externalConnection: {
						id: Number(selectedOption.key),
						name: selectedOption.label,
					},
				}));
			}
			return;
		}

		// Si name est activity, on doit mettre à jour l'objet complet
		if (name === 'activity') {
			const selectedOption = activityOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					activity: {
						id: Number(selectedOption.key),
					} as ActivityData,
				}));
			}
			return;
		}
		// Pour les autres champs, mise à jour standard
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Nettoyer l'activité sélectionnée
	const handleClearActivity = () => {
		setFormData((prev) => ({
			...prev,
			activity: null,
		}));
	};

	// Rendu du composant principal
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-8xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faArrowRightArrowLeft}
					title={
						id
							? "Édition d'un mouvement de badge"
							: 'Nouveau mouvement de badge'
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
							{/* COLONNE GAUCHE */}
							<CardSection
								icon={faInfoCircle}
								title="Informations non modifiables"
							>
								<InputField
									label="Numéro de badge importé"
									name="originalBadgeNumber"
									value={formData.originalBadgeNumber}
									onChange={handleChange}
									loading={loading}
									readOnly={true}
								/>
								<InputField
									label="Date et heure importées"
									name="originalDateTime"
									value={
										formatLocalDateTime(formData.originalDateTime, {
											year: 'numeric',
											month: '2-digit',
											day: '2-digit',
											hour: '2-digit',
											minute: '2-digit',
											second: '2-digit',
										}) || '—'
									}
									onChange={handleChange}
									loading={loading}
									readOnly={true}
								/>
								{/* originalRawData est un json */}
								<TextAreaField
									label="Données complètes importées"
									name="originalRawData"
									value={
										formData.originalRawData
											? JSON.stringify(formData.originalRawData, null, 2)
											: ''
									}
									onChange={handleChange}
									loading={loading}
									readOnly={true}
								/>
							</CardSection>

							{/* COLONNE DROITE */}
							<CardSection icon={faAddressCard} title="Informations de badge">
								<InputField
									label="Numéro de badge"
									name="badgeNumber"
									value={formData.badgeNumber || ''}
									onChange={handleChange}
									required
									loading={loading}
								/>
								<DatePickerField
									label="Date et heure"
									name="dateTime"
									value={parseLocalDate(formData.dateTime)}
									onChange={(date) =>
										setFormData({
											...formData,
											dateTime: date || undefined,
										})
									}
									loading={loading}
									showTime={true}
								/>
								<SelectField
									label="Sens (IN/OUT)"
									selectedKey={formData.direction || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'direction',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={directionOptions}
								/>
								<SelectField
									label="Statut"
									selectedKey={formData.status || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'status',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={statusOptions}
								/>
								{/* SELECT CONNEXION EXTERNE */}
								<SelectField
									label="Connexion externe utilisée"
									selectedKey={
										formData.externalConnection?.id?.toString() || ''
									}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'externalConnection',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={externalConnectionOptions}
									loading={loading}
									optionsLoading={loadingExternalConnections}
									readOnly={formData.status !== 'IMPORTED'}
									entityHref={
										formData.externalConnection?.id
											? `/app/settings/externalConnections/${formData.externalConnection.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											externalConnection: null,
										}))
									}
								/>
							</CardSection>

							<CardSection icon={faBuilding} title="Informations connexes">
								<InputField
									label="Prénom du propriétaire"
									name="ownerFirstName"
									value={formData.ownerFirstName || ''}
									onChange={handleChange}
									loading={loading}
								/>
								<InputField
									label="Nom du propriétaire"
									name="ownerLastName"
									value={formData.ownerLastName || ''}
									onChange={handleChange}
									loading={loading}
								/>
								<InputField
									label="Entreprise"
									name="company"
									value={formData.company || ''}
									onChange={handleChange}
									loading={loading}
								/>
								<InputField
									label="Affaire"
									name="project"
									value={formData.project || ''}
									onChange={handleChange}
									loading={loading}
								/>
								<SelectField
									label="Activité"
									selectedKey={formData.activity?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'activity',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={activityOptions}
									loading={loading}
									optionsLoading={loadingActivities}
									entityHref={
										formData.activity?.id
											? `/app/activities/${formData.activity.id}/edit`
											: undefined
									}
									onClear={handleClearActivity}
								/>
							</CardSection>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => navigate('/app/badgeMovements')}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
						extraActions={
							id ? (
								<SecondaryButton
									label={'Cloner'}
									onClick={() => {
										if (
											window.confirm(
												'Voulez-vous vraiment cloner ce mouvement de badge ?'
											)
										) {
											setFormData((prev) => ({
												...prev,
												id: 0,
												originalBadgeNumber: '',
												originalDateTime: undefined,
												originalRawData: undefined,
												importedAt: undefined,
												status: 'MANUAL',
											}));
											handleSave(true, true);
										}
									}}
									size="lg"
									disabled={isSubmitting}
								/>
							) : undefined
						}
					/>
				</div>
			</div>
		</div>
	);
};

export default BadgeMovementForm;
